import { useEffect, useMemo, useRef } from 'react';
import { generatePath, useHistory } from 'react-router-dom';

import { useFrontChatWidget } from '@kanda-libs/ks-design-library';

import { APP_ENV, URLS } from 'config';
import { Provider, useStatusContext } from 'pages/Status/context';
import { cleanObject } from 'utils';

interface StatusContentHook {
  provider?: Provider;
  isFinanced: boolean;
  isSigningDocuments: boolean;
  isLoading: boolean;
  isPaidByBank: boolean;
  isPaidByCard: boolean;
}

export default function useStatusContent(): StatusContentHook {
  const {
    provider,
    job,
    credit,
    isLoading,
    isPaidByBank,
    isPaidByCard,
    isFinanced,
  } = useStatusContext();
  const { push } = useHistory();
  const { update } = useFrontChatWidget();

  const isSigningDocuments = useMemo(() => {
    if (isLoading) return false;
    if (provider !== 'omni') return false;
    const jobFinanceStatus = job?.finance_status || null;
    const financeStatus = credit?.state || null;
    if (!jobFinanceStatus || !financeStatus) return false;
    return (
      jobFinanceStatus === 'applied_for_finance' && financeStatus === 'accepted'
    );
  }, [provider, job, credit, isLoading]);

  const statusRef = useRef<boolean>(false);
  // Effect redirects to job if not in a displayable status
  useEffect(() => {
    if (!job || statusRef.current) return;
    const id = job?.id;
    const status = job?.status;
    const financeStatus = job?.finance_status;
    if (!id) return;
    statusRef.current = true;
    if (status !== 'sent' || financeStatus !== 'not_applied') return;
    push(generatePath(URLS.job, { id }));
  }, [job, push, isLoading]);

  // const highlightRef = useRef<boolean>(false);
  // // Effect sends user info to Highlight
  // useEffect(() => {
  //   if (APP_ENV === 'qa' || !job || highlightRef.current) return;
  //   const customer = job?.customer;
  //   if (!customer) return;
  //   const email = customer?.email;
  //   const name = `${customer?.first_name} ${customer?.last_name}`;
  //   const phone = customer?.phone;
  //   const metadata = cleanObject({
  //     name,
  //     phone,
  //   });
  //   H.identify(email, metadata);
  //   highlightRef.current = true;
  // }, [job]);

  const frontRef = useRef<boolean>(false);
  // Effect updates frontchat
  useEffect(() => {
    if (APP_ENV === 'qa' || !job || frontRef.current) return;
    const customer = job?.customer;
    if (!customer) return;
    const email = customer?.email;
    const name = `${customer?.first_name} ${customer?.last_name}`;
    const phone = customer?.phone;
    const settings = cleanObject({
      email,
      name,
      phone,
    });
    update(settings);
    frontRef.current = true;
  }, [job, update]);

  return {
    isLoading,
    provider,
    isFinanced,
    isSigningDocuments,
    isPaidByBank,
    isPaidByCard,
  };
}
